import { useRef, useState, type CSSProperties, type FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { AuthFrame } from '../components/AuthFrame';
import { Rejection } from '../components/Rejection';
import { ApiError } from '../net/client';
import type { JoinTicket } from '../net/session';
import { invitationCodeLooksRight } from '../session/invitationCode';
import { bannerFor, type Refusal } from '../session/refusals';
import { useSession } from '../session/useSession';
import { fonts } from '../theme';
import {
  EMAIL,
  FieldBox,
  Notice,
  OtherWay,
  OtpEntry,
  PASSWORD_MAX,
  PASSWORD_MIN,
  PasswordBox,
  RefusalWords,
  StepHeading,
} from './SignIn';
import { borderFor, fieldDetails, input, primary, stepForm } from './signInForm';

/**
 * Joining a brand, unit L1. **No board draws it**: the layout is the user's
 * ruling in D29, built from sign-in's own parts.
 *
 * One form takes the invitation code, filled from the email's `?code=`, and
 * either a new account (email, name, password) or an existing one (email,
 * password). The API then asks for up to two mailed codes: one to prove the
 * new account's address, and one more for `accept_invitation`, which it asks
 * of everyone, `requireStepUp.ts`. Accepting makes the account the org's,
 * and the session's refresh carries the new role into a tenant's pair, so a
 * joined admin lands in the shell with no further step.
 *
 * Until then the account is a consumer, whose pair is never the portal's
 * session: it is a `JoinTicket`, held here and dropped on the way back.
 */
export function Join() {
  const session = useSession();
  const { search } = useLocation();
  const [code, setCode] = useState(() => new URLSearchParams(search).get('code') ?? '');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState<Mode>('create');
  const [step, setStep] = useState<Step>({ kind: 'form' });
  const [refusals, setRefusals] = useState<Partial<Record<Field, Refusal>>>({});
  const [banner, setBanner] = useState<Refusal | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const inFlight = useRef(false);

  const clear = (field: Field) =>
    setRefusals((r) => (r[field] === undefined ? r : { ...r, [field]: undefined }));

  /** Back to the form with what went wrong. The account exists by now, so it is sign-in. */
  function refuse(error: unknown) {
    setMode('signin');
    setNotice(null);
    setStep({ kind: 'form' });
    const known = refusalFor(error);
    setRefusals(known.fields);
    setBanner(known.banner);
  }

  /** Asks to accept: the API mails the last code. */
  async function accept(ticket: JoinTicket) {
    try {
      const result = await session.redeem(ticket, code);
      if (result.kind === 'challenge')
        setStep({ kind: 'confirm', ticket, challengeId: result.challengeId });
    } catch (error) {
      void session.dropTicket(ticket);
      refuse(error);
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (inFlight.current) return;
    const found: Partial<Record<Field, Refusal>> = {};
    if (code.trim() === '') found.code = { message: 'Enter the code from your invitation email.' };
    else if (!invitationCodeLooksRight(code)) found.code = mistyped;
    if (!EMAIL.test(email.trim()))
      found.email = { message: 'That does not look like an email address.' };
    if (mode === 'create') {
      if (name.trim() === '') found.name = { message: 'Enter your name.' };
      if (password.length < PASSWORD_MIN)
        found.password = { message: 'Password must be at least 8 characters.' };
      else if (password.length > PASSWORD_MAX)
        found.password = { message: 'Password must be at most 128 characters.' };
    } else if (password.length === 0) {
      found.password = { message: 'Enter your password.' };
    }
    setBanner(null);
    setRefusals(found);
    if (Object.keys(found).length > 0) return;

    inFlight.current = true;
    setSending(true);
    try {
      if (mode === 'create') {
        const challengeId = await session.register(email.trim(), password, name.trim());
        setStep({ kind: 'account', challengeId });
      } else {
        const start = await session.joinSignIn(email.trim(), password);
        if (start.kind === 'challenge')
          setStep({ kind: 'account', challengeId: start.challengeId });
        else await accept(start.ticket);
      }
    } catch (error) {
      if (error instanceof ApiError && error.code === 'email_taken') {
        setMode('signin');
        setNotice(accountExists);
      } else {
        const known = refusalFor(error);
        setRefusals(known.fields);
        setBanner(known.banner);
      }
    } finally {
      inFlight.current = false;
      setSending(false);
    }
  }

  function switchTo(next: Mode) {
    setMode(next);
    setNotice(null);
    setBanner(null);
    setRefusals({});
  }

  if (step.kind === 'account') {
    return (
      <OtpEntry
        email={email.trim()}
        onBack={() => setStep({ kind: 'form' })}
        submit={async (otp) => {
          const ticket = await session.joinVerify(step.challengeId, otp);
          await accept(ticket);
        }}
        resend={() => session.resendOtp(step.challengeId)}
      />
    );
  }

  if (step.kind === 'confirm') {
    const { ticket, challengeId } = step;
    return (
      <OtpEntry
        key={challengeId}
        email={email.trim()}
        title="Confirm joining"
        lead="We sent another code to"
        onBack={() => {
          void session.dropTicket(ticket);
          setMode('signin');
          setStep({ kind: 'form' });
        }}
        submit={async (otp) => {
          try {
            const result = await session.redeem(ticket, code, { challengeId, code: otp });
            if (result.kind === 'challenge')
              setStep({ kind: 'confirm', ticket, challengeId: result.challengeId });
          } catch (error) {
            // A wrong code stays here to be typed again; anything else is the form's.
            if (error instanceof ApiError && codeRefused.has(error.code)) throw error;
            void session.dropTicket(ticket);
            refuse(error);
          }
        }}
        resend={() => session.resendOtp(challengeId)}
      />
    );
  }

  const creating = mode === 'create';
  return (
    <AuthFrame>
      <form noValidate onSubmit={submit} style={stepForm}>
        <StepHeading title="Join your brand">
          {creating
            ? 'Enter the code from your invitation email, then create your account with the address it was sent to.'
            : 'Enter the code from your invitation email, then sign in with the address it was sent to.'}
        </StepHeading>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {banner !== null ? (
            <Rejection>
              <RefusalWords refusal={banner} />
            </Rejection>
          ) : notice !== null ? (
            <Notice>{notice}</Notice>
          ) : null}

          <FieldBox id="join-code" label="Invitation code" refusal={refusals.code}>
            <input
              className="vp-box"
              id="join-code"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                clear('code');
              }}
              autoComplete="off"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              disabled={sending}
              aria-invalid={refusals.code !== undefined}
              aria-describedby={refusals.code !== undefined ? 'join-code-refusal' : undefined}
              style={{ ...input, ...codeInput, borderColor: borderFor(refusals.code) }}
            />
          </FieldBox>

          <FieldBox id="join-email" label="Email" refusal={refusals.email}>
            <input
              className="vp-box"
              id="join-email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                clear('email');
              }}
              placeholder="amina@example.com"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              disabled={sending}
              aria-invalid={refusals.email !== undefined}
              aria-describedby={refusals.email !== undefined ? 'join-email-refusal' : undefined}
              style={{ ...input, borderColor: borderFor(refusals.email) }}
            />
          </FieldBox>

          {creating ? (
            <FieldBox id="join-name" label="Your name" refusal={refusals.name}>
              <input
                className="vp-box"
                id="join-name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  clear('name');
                }}
                autoComplete="name"
                disabled={sending}
                aria-invalid={refusals.name !== undefined}
                aria-describedby={refusals.name !== undefined ? 'join-name-refusal' : undefined}
                style={{ ...input, borderColor: borderFor(refusals.name) }}
              />
            </FieldBox>
          ) : null}

          <FieldBox id="join-password" label="Password" refusal={refusals.password}>
            <PasswordBox
              id="join-password"
              value={password}
              onChange={(value) => {
                setPassword(value);
                clear('password');
              }}
              autoComplete={creating ? 'new-password' : 'current-password'}
              disabled={sending}
              refusal={refusals.password}
            />
          </FieldBox>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <button
            className="vp-fill"
            type="submit"
            disabled={sending}
            aria-busy={sending}
            style={primary(!sending)}
          >
            {creating ? 'Create account' : 'Sign in and join'}
          </button>
          {creating ? (
            <OtherWay
              question="Already have an account?"
              action="Sign in"
              onClick={() => switchTo('signin')}
              disabled={sending}
            />
          ) : (
            <OtherWay
              question="New to Veriplus?"
              action="Create an account"
              onClick={() => switchTo('create')}
              disabled={sending}
            />
          )}
        </div>
      </form>
    </AuthFrame>
  );
}

type Mode = 'create' | 'signin';

type Step =
  | { kind: 'form' }
  | { kind: 'account'; challengeId: string }
  | { kind: 'confirm'; ticket: JoinTicket; challengeId: string };

type Field = 'code' | 'email' | 'name' | 'password';

/** OtpEntry's own refusal codes, which it reads as a wrong code. */
const codeRefused = new Set(['invalid_code', 'invalid_challenge']);

/** D29, invented: no source has a join flow to take words from. */
const accountExists = 'An account with this email already exists. Sign in with it to join.';

const mistyped: Refusal = {
  message: 'That invitation code has a typo.',
  hint: 'Check it against your email.',
};

/** The invitation refusals, shown in the API's own words, L1's row. */
const invitationRefusals = new Set(['already_in_organization', 'cannot_join_organization']);

/** What the API's answer means on the form: under a field, or in the rejection box. */
function refusalFor(error: unknown): {
  fields: Partial<Record<Field, Refusal>>;
  banner: Refusal | null;
} {
  if (error instanceof ApiError) {
    // One refusal for unknown, expired, used and someone else's, by the API's design.
    if (error.code === 'invalid_invitation')
      return { fields: { code: { message: error.message } }, banner: null };
    if (invitationRefusals.has(error.code))
      return { fields: {}, banner: { message: error.message } };
    if (error.code === 'invalid_credentials')
      return {
        fields: {
          password: {
            message: 'That email and password do not match.',
            hint: 'Check both and try again.',
          },
        },
        banner: null,
      };
  }
  const fields = fieldDetails(error, {
    code: 'token',
    email: 'email',
    name: 'name',
    password: 'password',
  });
  return fields !== null ? { fields, banner: null } : { fields: {}, banner: bannerFor(error) };
}

/**
 * The invitation code is 33 characters as the email prints it, and at the
 * field's drawn 16px with 16px sides it does not fit the 390 column. Seen at
 * 1440x900 in L1's live run: the last character was cut. The theme's mono, which
 * the code screens already use, holds every code to one width. D29.
 */
const codeInput: CSSProperties = { fontFamily: fonts.mono, fontSize: '15px', padding: '0 12px' };
